import type { OptionAction, OptionType } from '@steward/shared';

export interface SafetyLegInput {
  optionType: OptionType;
  strikePrice: number;
  action: OptionAction;
  quantityRemaining: number;
}

export interface SafetyThresholds {
  safePct: number;
  watchPct: number;
}

export const RISK_VIEW_THRESHOLDS: SafetyThresholds = { safePct: 5, watchPct: 2 };

export type SafetyLevel = 'safe' | 'watch' | 'danger';

export interface SideSafety {
  shortStrike: number; // max short put strike / min short call strike
  distance: number | null; // dollars OTM; negative = breached; null = no quote
  percent: number | null; // (distance / shortStrike) * 100; null = no quote
  level: SafetyLevel | null; // null = no quote
}

export interface PositionSafety {
  put: SideSafety | null; // null = no short puts
  call: SideSafety | null; // null = no short calls
  minPercent: number | null; // min of available side percents; null when no quote or no shorts
  closestSide: 'put' | 'call' | null;
  overall: SafetyLevel | 'no-quote' | 'no-shorts';
}

function isShortLeg(leg: SafetyLegInput): boolean {
  return (leg.action === 'STO' || leg.action === 'STC') && leg.quantityRemaining > 0;
}

export function levelForPercent(
  percent: number,
  thresholds: SafetyThresholds = RISK_VIEW_THRESHOLDS,
): SafetyLevel {
  if (percent < thresholds.watchPct) return 'danger';
  if (percent <= thresholds.safePct) return 'watch';
  return 'safe';
}

function buildSide(
  shortStrike: number,
  currentPrice: number | null,
  distanceFromStrike: (currentPrice: number, shortStrike: number) => number,
  thresholds: SafetyThresholds,
): SideSafety {
  if (currentPrice === null) {
    return { shortStrike, distance: null, percent: null, level: null };
  }
  const distance = distanceFromStrike(currentPrice, shortStrike);
  const percent = (distance / shortStrike) * 100;
  return { shortStrike, distance, percent, level: levelForPercent(percent, thresholds) };
}

export function analyzeShortStrikeSafety(
  legs: SafetyLegInput[],
  currentPrice: number | null,
  thresholds: SafetyThresholds = RISK_VIEW_THRESHOLDS,
): PositionSafety {
  const shortPutStrikes = legs
    .filter((leg) => leg.optionType === 'put' && isShortLeg(leg))
    .map((leg) => leg.strikePrice);
  const shortCallStrikes = legs
    .filter((leg) => leg.optionType === 'call' && isShortLeg(leg))
    .map((leg) => leg.strikePrice);

  const put =
    shortPutStrikes.length > 0
      ? buildSide(
          Math.max(...shortPutStrikes),
          currentPrice,
          (price, strike) => price - strike,
          thresholds,
        )
      : null;

  const call =
    shortCallStrikes.length > 0
      ? buildSide(
          Math.min(...shortCallStrikes),
          currentPrice,
          (price, strike) => strike - price,
          thresholds,
        )
      : null;

  const percents = [put?.percent, call?.percent].filter(
    (percent): percent is number => percent !== null && percent !== undefined,
  );
  const minPercent = percents.length > 0 ? Math.min(...percents) : null;

  let closestSide: 'put' | 'call' | null = null;
  if (put?.percent != null && call?.percent != null) {
    closestSide = put.percent <= call.percent ? 'put' : 'call';
  } else if (put?.percent != null) {
    closestSide = 'put';
  } else if (call?.percent != null) {
    closestSide = 'call';
  }

  let overall: SafetyLevel | 'no-quote' | 'no-shorts';
  if (put === null && call === null) {
    overall = 'no-shorts';
  } else if (currentPrice === null || minPercent === null) {
    overall = 'no-quote';
  } else {
    overall = levelForPercent(minPercent, thresholds);
  }

  return { put, call, minPercent, closestSide, overall };
}
